/**
 * Analyzer: Mistake Taxonomy
 *
 * Finds mistakes that recurred across multiple trades in the session.
 * A repeated mistake is the dominant failure mode — more actionable
 * than a one-off slip because it's a pattern, not an accident.
 *
 * Threshold: appears in >= max(2, 50% of trades) to count as "dominant".
 */

import { CATEGORY, SEVERITY, makeObservation } from '../types';
import { countAcrossTrades } from '../utils';

export const key = 'mistakeTaxonomy';

export const analyze = (ctx) => {
  const { session, trades, allMistakes = [] } = ctx;
  if (!session || !Array.isArray(trades) || trades.length === 0) return null;

  const counts = countAcrossTrades(trades, 'mistakes');
  if (counts.size === 0) return null;

  const threshold = Math.max(2, Math.ceil(trades.length * 0.5));
  const shared = Array.from(counts.entries())
    .filter(([, cnt]) => cnt >= threshold)
    .sort((a, b) => b[1] - a[1]);

  if (shared.length === 0) return null;

  const [topMistakeId, topCount] = shared[0];
  // Mistake IDs may be strings (user-typed) OR reference ids in allMistakes.
  // Try to resolve a display name; fall back to the raw value.
  const nameFromLib = allMistakes.find(m => m.id === topMistakeId || m.name === topMistakeId)?.name;
  const mistakeLabel = nameFromLib || topMistakeId;

  // If multiple mistakes are shared, note the runner-up in detail
  const secondary = shared[1]
    ? (allMistakes.find(m => m.id === shared[1][0] || m.name === shared[1][0])?.name || shared[1][0])
    : null;
  const secondaryClause = secondary ? ` "${secondary}" also appeared in ${shared[1][1]}.` : '';

  return makeObservation({
    id: `${key}:${session.id}:${topMistakeId}`,
    analyzer: key,
    scope: 'session',
    scopeId: session.id,
    severity: SEVERITY.WARNING,
    category: CATEGORY.DISCIPLINE,
    title: `"${mistakeLabel}" is the dominant failure mode`,
    detail: `Appeared in ${topCount} of ${trades.length} trades.${secondaryClause}`,
    action: `Write one specific rule that would have prevented "${mistakeLabel}" — pin it in Lessons.`,
    evidence: { topMistake: mistakeLabel, topCount, total: trades.length, all: shared },
    metric: { label: 'Frequency', value: topCount, baseline: trades.length, unit: 'of' },
    confidence: Math.min(0.95, 0.6 + (topCount / trades.length) * 0.4),
    priority: 80
  });
};
